import React, { memo, ReactNode } from 'react';

import styled from 'styled-components';

import { IResumeFields, Languages } from 'context';
import { EducationBlock } from 'organisms/EducationBlock';
import { ExperienceBlock } from 'organisms/ExperienceBlock';
import { LanguageBlock } from 'organisms/LanguageBlock';
import { ProfileBasicsBlock } from 'organisms/ProfileBasicsBlock';
import { ProfileCertificatesBlock } from 'organisms/ProfileCertificatesBlock';
import { ProfileProjectsBlock } from 'organisms/ProfileProjectsBlock';
import { ICvSkillSuggestion, ProfileSkillsBlock } from 'organisms/ProfileSkillsBlock';
import type { ICareerProfileFormValues } from './types';

export interface ICareerProfileForm {
  values: ICareerProfileFormValues;
  fields: IResumeFields;
  /** Language for the reused date pickers — English keeps months deterministic. */
  dateLanguage: Languages;
  onChange: (name: string, value: unknown) => void;
  /** Предпросмотр методиста (ТЗ 0039 доводка): сохранять нечего и некуда. */
  readOnly?: boolean;
  /** Фото — маленький аватар в данных (0072), а не отдельная большая секция. */
  photo?: ReactNode;
  /** «O mnie» по языку документа — второй раздел формы. */
  cvText?: ReactNode;
  /** Навыки, упомянутые в CV без уровня (0072). */
  skillSuggestions?: ICvSkillSuggestion[];
}

/**
 * Разделы редактора CV в порядке документа (0072, шаг 2). `id` — якорь
 * навигации по разделам и адресов вида `/career/profile#cv-projects`.
 */
export const CV_SECTIONS = [
  { id: 'cv-basics', label: 'careerProfile.sections.basics' },
  { id: 'cv-about', label: 'careerProfile.sections.about' },
  { id: 'cv-skills', label: 'careerProfile.sections.skills' },
  { id: 'cv-projects', label: 'careerProfile.sections.projects' },
  { id: 'cv-experience', label: 'careerProfile.sections.experience' },
  { id: 'cv-education', label: 'careerProfile.sections.education' },
  { id: 'cv-languages', label: 'careerProfile.sections.languages' },
  { id: 'cv-certificates', label: 'careerProfile.sections.certificates' },
] as const;

export type CvSectionId = typeof CV_SECTIONS[number]['id'];

/**
 * The language-neutral profile editor (spec 0018, increment B). Reuses the
 * Experience / Education / Language blocks from the old resume, repointed at the
 * new profile model; skills, projects and certificates are new.
 *
 * 0072: своей кнопки сохранения больше нет — одна «Zapisz zmiany» в панели над
 * редактором сохраняет и форму, и тексты CV, и фото.
 */
export const CareerProfileForm = memo(
  ({
    values,
    fields,
    dateLanguage,
    onChange,
    readOnly = false,
    photo,
    cvText,
    skillSuggestions,
  }: ICareerProfileForm) => (
    <Wrapper>
      {/* 🔴 Один `fieldset disabled` вместо семи пропов `readOnly`: браузер
          гасит ВСЕ поля внутри сам, и забыть новый блок физически нельзя.
          Раньше пряталась только кнопка «Сохранить», а форма оставалась
          живой — плашка обещала «изменения не сохраняются», интерфейс
          приглашал их вносить. */}
      <Fields disabled={readOnly}>
        <Section id="cv-basics">
          <ProfileBasicsBlock values={values} onChange={onChange} photo={photo} />
        </Section>

        <Section id="cv-about">{cvText}</Section>

        <Section id="cv-skills">
          <ProfileSkillsBlock
            skills={values.skills}
            customSkills={values.customSkills}
            suggestions={skillSuggestions}
            onChangeSkills={(skills) => onChange('skills', skills)}
            onChangeCustomSkills={(customSkills) => onChange('customSkills', customSkills)}
          />
        </Section>

        <Section id="cv-projects">
          <ProfileProjectsBlock
            projects={values.projects}
            onChange={(projects) => onChange('projects', projects)}
          />
        </Section>

        <Section id="cv-experience">
          <ExperienceBlock
            language={dateLanguage}
            fields={fields}
            values={values.experience}
            onExperienceChange={onChange}
            onInputChange={onChange}
          />
        </Section>

        <Section id="cv-education">
          <EducationBlock
            language={dateLanguage}
            fields={fields}
            values={values.education}
            onChangeEducation={onChange}
            onInputChange={onChange}
          />
        </Section>

        <Section id="cv-languages">
          <LanguageBlock
            fields={fields}
            languagesList={values.languages}
            onChangeLanguages={onChange}
            onInputChange={onChange}
          />
        </Section>

        <Section id="cv-certificates">
          <ProfileCertificatesBlock
            certificates={values.certificates}
            onChange={(certificates) => onChange('certificates', certificates)}
          />
        </Section>
      </Fields>
    </Wrapper>
  ),
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Собственных рамок и отступов у fieldset быть не должно — он здесь
   исключительно ради нативного `disabled`, а не ради вида. */
const Fields = styled.fieldset`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

/* Якорь раздела: при переходе по навигации заголовок не прячется под
   закреплённой панелью сохранения. */
const Section = styled.section`
  scroll-margin-top: 96px;
`;
